import React, { useState } from 'react';

import { Button, MultiSelect, Select, Text, Stack, createStyles, Drawer } from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronDown } from '@tabler/icons-react';
import EventTypeMultiSelectAutoComplete, {
  IEventTypeMultiSelectAutoComplete,
} from 'components/form-fields/auto-complete-fields/EventTypeMultiSelectAutoComplete';
import { MRT_RowSelectionState } from 'mantine-react-table';
import {
  ReservationType,
  SearchFilters,
  WeekdayType,
} from 'pages/private/owner/AthleteDetail/components/Reservations/types';
import { AthleteEvent } from 'types';

const useStyles = createStyles(theme => ({
  header: {
    display: 'grid',
    gridTemplateColumns: 'auto auto min-content min-content',
    gridTemplateAreas: `'title title filters delete'`,
    alignItems: 'center',
    gap: theme.spacing.sm,
    paddingBottom: theme.spacing.sm,
  },
  title: {
    gridArea: 'title',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    marginLeft: theme.spacing.lg,
  },
  filters: {
    gridArea: 'filters',
  },
  delete: {
    gridArea: 'delete',
  },
  button: {
    width: 'min-content',
    marginLeft: 'auto',
  },
}));

const Weekdays: Array<{ label: WeekdayType['weekDay']; value: WeekdayType['day'] }> = [
  { value: '1', label: 'Sunday' },
  { value: '2', label: 'Monday' },
  { value: '3', label: 'Tuesday' },
  { value: '4', label: 'Wednesday' },
  { value: '5', label: 'Thursday' },
  { value: '6', label: 'Friday' },
  { value: '7', label: 'Saturday' },
];

const Reservations: Array<{ label: ReservationType['label']; value: ReservationType['type'] }> = [
  { value: 'true', label: 'Bulk Add' },
  { value: 'false', label: 'Not Bulk Add' },
];

interface IAthleteReservationsFiltersProps {
  searchFilters: SearchFilters;
  setSearchFilters: React.Dispatch<React.SetStateAction<SearchFilters>>;
  initialSearchFiltersValue: SearchFilters;
  tableData: AthleteEvent[];
  handleDeleteBulkAthleteEvents: () => void;
  openTabsDrawer: () => void;
  rowSelection: MRT_RowSelectionState;
}

const AthleteReservationsFilters = ({
  searchFilters,
  setSearchFilters,
  initialSearchFiltersValue,
  openTabsDrawer,
  handleDeleteBulkAthleteEvents,
  rowSelection,
}: IAthleteReservationsFiltersProps): JSX.Element => {
  const { classes } = useStyles();
  const today = new Date();
  const [isFiltersOpen, { open, close }] = useDisclosure(false);

  const [eventTypes, setEventTypes] = useState<IEventTypeMultiSelectAutoComplete[]>([]);

  const handleDateChange = (value: DatesRangeValue): void => {
    setSearchFilters(prev => ({ ...prev, date: value }));
  };
  const handleEventsChange = (values: IEventTypeMultiSelectAutoComplete[]): void => {
    setEventTypes(values);
    setSearchFilters(prev => ({ ...prev, events: values.map(v => v.eventTypeId) }));
  };

  const handleWeekdaysChange = (values: WeekdayType['day'][]): void => {
    setSearchFilters(prev => ({ ...prev, weekdays: values }));
  };

  const handleReservationTypeChange = (value: ReservationType['type'] | null): void => {
    setSearchFilters(prev => ({ ...prev, isBulkAdd: value }));
  };

  const handleClearFilters = (): void => {
    setSearchFilters(initialSearchFiltersValue);
    setEventTypes([]);
  };

  return (
    <div className={classes.header}>
      <Text size='md' fw='bold' className={classes.title} onClick={openTabsDrawer}>
        Reservations
        <IconChevronDown size={16} style={{ marginLeft: 4 }} />
      </Text>
      <Button onClick={open} className={classes.filters} size='xs'>
        Filters
      </Button>
      <Button
        className={classes.delete}
        onClick={handleDeleteBulkAthleteEvents}
        disabled={!Object.keys(rowSelection).length}
        color='red'
        size='xs'
      >
        Delete
      </Button>
      <Drawer position='bottom' opened={isFiltersOpen} onClose={close} zIndex={999}>
        <Stack spacing='sm' justify='flex-start'>
          <Button color='red' onClick={handleClearFilters} className={classes.button}>
            Clear Filters
          </Button>
          <MultiSelect
            value={searchFilters.weekdays}
            onChange={event => handleWeekdaysChange(event as WeekdayType['day'][])}
            data={Weekdays}
            placeholder='Select weekdays'
          />
          <Select
            value={searchFilters.isBulkAdd}
            onChange={event => handleReservationTypeChange(event as ReservationType['type'])}
            data={Reservations}
            placeholder='Select reservation type'
            clearable
          />
          <DatePickerInput
            value={searchFilters.date}
            onChange={handleDateChange}
            valueFormat='YYYY MMM DD'
            type='range'
            allowSingleDateInRange
            clearable
            {...{ placeholder: 'Select date range' }}
            minDate={today}
          />
          <EventTypeMultiSelectAutoComplete handleChange={handleEventsChange} values={eventTypes} />
        </Stack>
      </Drawer>
    </div>
  );
};

export default AthleteReservationsFilters;
